import { useEffect, useState } from "react";
import qapi from "views_shared/app/javascript/api";
import { TEAM_ROSTER_PAGE_SIZE } from "views_shared/app/javascript/api/company_jobs";
import qlog from "views_shared/app/javascript/scripts/qlog";
import { expertUrl, prProfileUrl } from "views_shared/app/javascript/utils/urls";

// Alyssa's design sizes the cluster for eight avatars plus a "+N" pill.
export const MAX_TEAM_AVATARS = 8;

const EXPERT_TYPE = "Id::PromoEntity::Expert";

// Which of these profiles can this viewer actually OPEN? The roster and the profile page are gated by
// different policies: the roster view lets a company's representative read its employment rows, while the
// profile view shows a hidden profile only to its owner or an admin. So a representative can legitimately
// receive a colleague whose profile page would answer "This profile isn't available" — an unconditional
// dead link. Re-asking the profile view (pr_profiles or experts) for just the ids we are about to render
// settles it: RLS silently drops the ones this viewer can't open, so the surviving ids are exactly the
// linkable ones.
//
// Failure is not fatal: an unlinked avatar is a perfectly good avatar, and losing the whole card over a
// decorative link would be the worse trade. Both failure shapes log and fall back to "nothing linkable".
const linkableProfileIds = async (profilesApi, ids) => {
  if (!ids.length) return new Set();

  try {
    const response = await profilesApi.getAllById(ids, { select: "id" });
    if (response?.error) {
      qlog.error("Team Members linkability check failed:", response.error);
      return new Set();
    }
    const rows = Array.isArray(response?.data) ? response.data : [];
    // Ids arrive as numbers here and as JSON numbers there, but compare as strings so a future
    // string-id source can't silently unlink everyone.
    return new Set(rows.map((row) => String(row.id)));
  } catch (error) {
    qlog.error("Team Members linkability check failed:", error);
    return new Set();
  }
};

/**
 * useTeamMembers — data half of the "Team Members" rail card on PR and expert profiles.
 *
 * Reads one company's current experts and PR people through the `company_jobs` view and shapes them
 * into avatar-stack items. Which company is NOT this hook's decision: the page picks the employment
 * (see PrProfile / Expert) and passes its `companyId` plus the ids to leave out — the profile being
 * viewed, who is not their own colleague. The hook idles (settling empty, so the page can collapse the
 * rail) until it has a `companyId`; it does not also require the exclusions.
 *
 * The view's RLS decides per-viewer which colleagues exist at all, so the hook simply keeps whatever
 * comes back. Rows are JOBS: nothing makes (person, company) unique, so a colleague holding two
 * current jobs at the company arrives twice. The hook collapses rows to people — keyed by type AND id,
 * since expert and PR ids come from different tables — first occurrence winning, which the server
 * order makes the most-curated one. It then slices the design's cap and reports the distinct total
 * (plus any rows past the one page it read) so the card's pill counts PEOPLE left out, not rows.
 *
 * Two reads, not one: the second (see linkableProfileIds) decides which of the capped avatars may carry
 * a link. State is committed once, after both, so no link ever appears and then disappears.
 *
 * Never throws: a decorative rail card must not take the profile page down, so both failure shapes
 * (`getResource` resolving `{ error }` on a non-2xx, and a rejected transport promise) log and settle
 * empty. That is deliberately unlike the editing surfaces, where a false empty would mislead an editor.
 *
 * @param {Object} options
 * @param {number|string} [options.companyId] - The employer whose team to list
 * @param {number|string} [options.excludePromoterId] - Promoter profile id to omit (the viewed PR
 *   profile, or a self-representing expert's own PR profile). Optional: falsy means "exclude nothing".
 * @param {number|string} [options.excludeExpertId] - Expert id to omit (the viewed expert). Optional.
 * @param {boolean} [options.expertsFirst] - List experts before PR people (the expert page's own kind).
 * @returns {{ members: Array, totalCount: number, isLoading: boolean }} `members` are AvatarStack
 *   items (`{ key, name, avatarUrl, href? }`), capped at MAX_TEAM_AVATARS — `href` is present only for
 *   colleagues this viewer can open; `totalCount` is the distinct-people total behind them
 */
function useTeamMembers({ companyId, excludePromoterId, excludeExpertId, expertsFirst }) {
  const [members, setMembers] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // No employer to ask about (profile still loading, or no qualifying employment): settle empty
    // rather than leaving the card in a skeleton that never resolves. The exclusions are NOT part of
    // this guard — companyJobs.getTeamProfilesByCompany treats a falsy id as "exclude nothing", so a
    // caller with no such id (no self-representing profile) still gets a roster.
    if (!companyId) {
      setMembers([]);
      setTotalCount(0);
      setIsLoading(false);
      return undefined;
    }

    // Employment edits are optimistic-with-rollback, so the picked company can flip A -> B -> A with
    // reads in flight. This flag (the use_viewers_also_view idiom) is per-effect-run and keyed on both
    // inputs, so a superseded run can never commit over the current company's team — including the
    // A -> B -> A case, where comparing the response's company to the current one would NOT be enough.
    // It is re-checked after each of the run's two awaits.
    let componentIsMounted = true;

    const load = async () => {
      try {
        setIsLoading(true);

        const response = await qapi.companyJobs.getTeamProfilesByCompany(companyId, {
          excludePromoterId,
          excludeExpertId,
          expertsFirst,
        });
        if (!componentIsMounted) return;

        // getResource RESOLVES `{ error }` on a non-2xx instead of throwing, so THIS branch — not the
        // catch below — is the one an ordinary PostgREST failure takes.
        if (response?.error) {
          qlog.error("Team Members lookup failed:", response.error);
          setMembers([]);
          setTotalCount(0);
          return;
        }

        const rows = Array.isArray(response?.data) ? response.data : [];
        const peopleByKey = new Map();
        rows.forEach((row) => {
          if (row.person_id == null) return;
          const isExpert = row.person_type === EXPERT_TYPE;
          const key = `${isExpert ? "expert" : "pr"}-${row.person_id}`;
          if (peopleByKey.has(key)) return;
          peopleByKey.set(key, {
            key,
            name: row.person_name,
            avatarUrl: row.person_profile_image_url,
            personId: row.person_id,
            isExpert,
            slug: row.person_slug,
            migrateId: row.person_migrate_id,
          });
        });

        const people = [...peopleByKey.values()];
        const shown = people.slice(0, MAX_TEAM_AVATARS);
        const idsOf = (wantExperts) =>
          shown.filter((person) => person.isExpert === wantExperts).map((person) => person.personId);

        const [linkablePrs, linkableExperts] = await Promise.all([
          linkableProfileIds(qapi.prProfiles, idsOf(false)),
          linkableProfileIds(qapi.experts, idsOf(true)),
        ]);
        if (!componentIsMounted) return;

        setMembers(
          shown.map(({ personId, isExpert, slug, migrateId, ...person }) => {
            const linkable = (isExpert ? linkableExperts : linkablePrs).has(String(personId));
            // A promoter with employment of their own is never a self-expert, so /pr_profiles is their
            // canonical path (/profiles would 301 here). No slug or no entitlement -> a plain avatar.
            const profileUrl = isExpert ? expertUrl : prProfileUrl;
            return { ...person, href: slug && linkable ? profileUrl({ slug, migrate_id: migrateId }) : undefined };
          }),
        );
        const rowsPastThisPage =
          rows.length < TEAM_ROSTER_PAGE_SIZE ? 0 : Math.max(0, (response.totalRows || 0) - rows.length);
        setTotalCount(people.length + rowsPastThisPage);
      } catch (error) {
        // The transport itself failed (network down, token refresh threw) — a rejection, not `{ error }`.
        qlog.error("Team Members lookup failed:", error);
        if (componentIsMounted) {
          setMembers([]);
          setTotalCount(0);
        }
      } finally {
        if (componentIsMounted) setIsLoading(false);
      }
    };
    load();

    return () => {
      componentIsMounted = false;
    };
  }, [companyId, excludePromoterId, excludeExpertId, expertsFirst]);

  return { members, totalCount, isLoading };
}

export default useTeamMembers;
